"use client";

import { useState } from "react";
import { useIsomorphicLayoutEffect } from "~/hooks/useIsomorphicLayoutEffect";
import { QUIZ_FUNNEL_MAP, QUIZ_ROUTE_TO_CATEGORY } from "~/constants/quizFunnel";
import { resolveQuizRedirectURL } from "~/utils/resolveQuizRedirect";
import { QUIZ_CATEGORIES } from "~/constants/questionnaire";
import { PRODUCT } from "~/constants/products";
import { LIBIDA_PDP } from "~/constants/flags";
import { hasFaceCreamInCart } from "../cart";
import { useCartStore } from "~/stores/useCartStore";
import useQuizCompleted from "~/hooks/useQuizCompleted";
import useSkipQuiz from "~/hooks/useSkipQuiz";
import useFlag from "~/hooks/useFlag";

/**
 * Resolves where a quiz CTA should link to *before* navigating: the given quiz
 * start page href if the user still has to take it, or straight to the quiz's real
 * destination if not (skipping the quiz page entirely, so there's no flicker
 * between them). The destination itself comes from `resolveQuizRedirectURL`, the
 * same rule the quiz pages apply when they redirect.
 *
 * Completion is only knowable client-side (localStorage / auth), so the returned
 * href is the given quiz route until mount (matching SSR) and the resolved one
 * afterwards — computing it directly during render would cause a server/client
 * hydration mismatch that React logs but does not correct, leaving the wrong
 * (stale) href in the DOM. Swapping post-mount is invisible to the user since it
 * happens before any click.
 *
 * This is the primitive: components that render their own link element (header
 * CTAs, product cards) call it directly, while plain `componentsDS/Button` CTAs use
 * the `QuizCTA` wrapper in `components/features/quiz` instead of repeating it.
 *
 * @param href - A plain quiz route, e.g. `QUIZ_ROUTES.ANTI_AGING_FACE_CREAM`.
 */
export const useQuizLink = (href: string) => {
    // "" is itself a valid category (the main hormone-imbalance/oestra quiz), so an
    // unrecognized href must stay distinguishable from that: isKnownRoute, not the
    // category, is what every decision below is gated on. The `?? ""` only satisfies
    // noUncheckedIndexedAccess — isKnownRoute already proved the key is present.
    const isKnownRoute = Object.hasOwn(QUIZ_ROUTE_TO_CATEGORY, href);
    const category = isKnownRoute ? QUIZ_ROUTE_TO_CATEGORY[href] ?? "" : "";

    // Hooks must run unconditionally regardless of isKnownRoute; their results are
    // only trusted below when the route is actually recognized.
    const { isCompleted, isChecking } = useQuizCompleted(category);
    const isQuizSkipped = useSkipQuiz(category);
    const isLibidaPdpEnabled = !!useFlag(LIBIDA_PDP);
    // One cart subscription per CTA, not one per rule — a landing page renders
    // several of these.
    const cartItems = useCartStore((s) => s.cartItems);

    const hasCheckoutOverrideProduct = hasFaceCreamInCart(category, cartItems);
    const hasLibidaInCart = cartItems.some((item) => item.slug === PRODUCT.LIBIDA);
    const isLibidaPdp = isKnownRoute && category === QUIZ_CATEGORIES.LIBIDA && isLibidaPdpEnabled;

    const shouldSkip = isKnownRoute && (isQuizSkipped || isCompleted || isLibidaPdp);
    const pqpRoute = isKnownRoute ? QUIZ_FUNNEL_MAP[category]?.pqpRoute : undefined;

    const resolvedHref = shouldSkip && pqpRoute
        ? resolveQuizRedirectURL(pqpRoute, { hasCheckoutOverrideProduct, isLibidaPdp, hasLibidaInCart })
        : href;

    const [isMounted, setIsMounted] = useState(false);

    // Layout timing, so the resolved href is in the DOM before the browser paints
    // rather than a frame later where an early click still hits the quiz route.
    useIsomorphicLayoutEffect(() => {
        setIsMounted(true);
    }, []);

    return { href: isMounted ? resolvedHref : href, isCompleted: shouldSkip, isChecking: isKnownRoute && isChecking };
};

export default useQuizLink;
